import {
	BoxSwitch,
	Button,
	DropdownFull,
	Input,
	InputPhoneFull,
	Typography
} from 'components'
import {
	DropdownFullItemProps,
	GoodLinkFormInputs,
	GoodLinkProps,
	Icons,
	TypographyTypes
} from 'interfaces'
import { typeOfLinkData } from 'lib'
import React, { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import {
	allValuesNonEmpty,
	parseToDisplay,
	parseToStorage,
	PHONE_PLACEHOLDER
} from 'utils'

const POSSIBLE_LINK_OPTIONS = [1, 2, 3]
const URL_PATTERN = /^(http|https|ftp):\/\/[^\s/$.?#].[^\s]*$/
const WATCH_FIELDS = [
	'label',
	'url',
	'phoneNumber',
	'subLabel',
	'subUrl',
	'subPhoneNumber'
] as const

export const GoodLink: React.FC<GoodLinkProps> = ({
	totalData,
	setOpenSlide,
	setTotalData,
	onClickClose,
	deliveryActive,
	setDeliveryActive,
	mode
}): JSX.Element => {
	const [typeOfLink, setTypeOfLink] = useState<DropdownFullItemProps>(
		typeOfLinkData[0]
	)
	const [typeOfSubLink, setTypeOfSubLink] = useState<DropdownFullItemProps>(
		typeOfLinkData[0]
	)
	const [subLink, setSubLink] = useState(false)
	const [clean, setClean] = useState(false)
	const [subClean, setSubClean] = useState(false)
	const {
		register,
		watch,
		setValue,
		handleSubmit,
		formState: { errors }
	} = useForm<GoodLinkFormInputs>({
		mode: 'all'
	})

	const [label, url, phoneNumber, subLabel, subUrl, subPhoneNumber] =
		watch(WATCH_FIELDS)

	const rules = {
		url: {
			required: { value: true, message: 'This field is required' },
			maxLength: {
				value: 512,
				message: 'Maximum characters exceeded'
			},
			pattern: {
				value: URL_PATTERN,
				message: 'Invalid link'
			}
		},
		email: {
			required: { value: true, message: 'This field is required' },
			maxLength: { value: 60, message: 'Maximum characters exceeded' }
		},
		label: {
			required: { value: true, message: 'This field is required' },
			maxLength: { value: 25, message: 'Maximum characters exceeded' }
		},
		phoneNumber: {
			required: { value: true, message: 'This field is required' },
			maxLength: { value: 18, message: 'Maximum characters exceeded' }
		},
		subUrl: {
			required: { value: subLink, message: 'This field is required' },
			maxLength: {
				value: 512,
				message: 'Maximum characters exceeded'
			},
			pattern: {
				value: URL_PATTERN,
				message: 'Invalid link'
			}
		},
		subEmail: {
			required: { value: subLink, message: 'This field is required' },
			maxLength: { value: 60, message: 'Maximum characters exceeded' }
		},
		subLabel: {
			required: { value: subLink, message: 'This field is required' },
			maxLength: { value: 25, message: 'Maximum characters exceeded' }
		},
		subPhoneNumber: {
			required: { value: subLink, message: 'This field is required' },
			maxLength: { value: 18, message: 'Maximum characters exceeded' }
		}
	}

	const onSubmit: SubmitHandler<GoodLinkFormInputs> = (values): void => {
		if (typeOfLink.id !== 0) {
			if (!subLink) {
				setTotalData({
					...totalData,
					delivery_enhancements: {
						...totalData.delivery_enhancements,
						good_link: {
							...totalData.delivery_enhancements.good_link,
							link_type: typeOfLink.value || '',
							link_value: values.label,
							link_call_action:
								typeOfLink.value === 'phone'
									? parseToStorage(values.phoneNumber)
									: values.url,
							is_sublink: subLink,
							sublink_type: '',
							sublink_value: '',
							sublink_call_action: ''
						}
					}
				})
			} else {
				setTotalData({
					...totalData,
					delivery_enhancements: {
						...totalData.delivery_enhancements,
						good_link: {
							...totalData.delivery_enhancements.good_link,
							link_type: typeOfLink.value || '',
							link_value: values.label,
							link_call_action:
								typeOfLink.value === 'phone'
									? parseToStorage(values.phoneNumber)
									: values.url,
							is_sublink: subLink,
							sublink_type: typeOfSubLink.value || '',
							sublink_value: values.subLabel,
							sublink_call_action:
								typeOfSubLink.value === 'phone'
									? parseToStorage(values.subPhoneNumber)
									: values.subUrl
						}
					}
				})
			}
		} else {
			setTotalData({
				...totalData,
				delivery_enhancements: {
					...totalData.delivery_enhancements,
					no_good_link: true,
					good_link: {
						...totalData.delivery_enhancements.good_link,
						link_type: '',
						link_value: '',
						link_call_action: '',
						is_sublink: false,
						sublink_type: '',
						sublink_value: '',
						sublink_call_action: ''
					}
				}
			})
		}

		setOpenSlide(false)
		setDeliveryActive({
			...deliveryActive,
			goodLink: true
		})
	}

	useEffect(() => {
		if (clean) {
			setValue('url', '')
			setValue('label', '')
			setValue('phoneNumber', '')
		}
	}, [typeOfLink])

	useEffect(() => {
		if (subClean) {
			setValue('subUrl', '')
			setValue('subLabel', '')
			setValue('subPhoneNumber', '')
		}
	}, [typeOfSubLink])

	useEffect(() => {
		if (!subLink) {
			setValue('subUrl', '')
			setValue('subLabel', '')
			setValue('subPhoneNumber', '')
		}
	}, [subLink])

	const isSubmit = useMemo((): boolean => {
		if (
			typeOfLink.id === 0 &&
			!allValuesNonEmpty({ label, subLabel, phoneNumber, subPhoneNumber, url })
		) {
			if (
				(mode === 'edit' || mode === 'create') &&
				(totalData.delivery_enhancements.good_link.is_sublink ||
					totalData.delivery_enhancements.good_link.link_call_action ||
					totalData.delivery_enhancements.good_link.link_value)
			) {
				return true
			}
			return false
		}
		if (!subLink) {
			if (
				POSSIBLE_LINK_OPTIONS.includes(typeOfLink.id as number) &&
				label &&
				url
			) {
				return true
			}
			if (typeOfLink.id === 4 && allValuesNonEmpty({ label, phoneNumber })) {
				return true
			}
			return false
		}
		if (
			POSSIBLE_LINK_OPTIONS.includes(typeOfLink.id as number) &&
			POSSIBLE_LINK_OPTIONS.includes(typeOfSubLink.id as number) &&
			allValuesNonEmpty({ label, subLabel, url, subUrl })
		) {
			return true
		}
		if (
			(POSSIBLE_LINK_OPTIONS.includes(typeOfLink.id as number) ||
				typeOfLink.id === 4) &&
			typeOfSubLink.id === 4 &&
			allValuesNonEmpty({ label, subLabel, subPhoneNumber })
		) {
			return true
		}
		if (
			typeOfLink.id === 4 &&
			typeOfSubLink.id === 4 &&
			allValuesNonEmpty({ label, subLabel, phoneNumber, subPhoneNumber })
		) {
			return true
		}
		if (
			typeOfLink.id === 4 &&
			POSSIBLE_LINK_OPTIONS.includes(typeOfSubLink.id as number) &&
			allValuesNonEmpty({ phoneNumber, label, subUrl, subLabel })
		) {
			return true
		}
		return false
	}, [
		subLink,
		label,
		url,
		phoneNumber,
		typeOfLink,
		typeOfSubLink,
		subLabel,
		subUrl,
		subPhoneNumber,
		totalData,
		mode
	])

	useEffect(() => {
		const auxTypeLink = typeOfLinkData.find(
			item => item.value === totalData.delivery_enhancements.good_link.link_type
		)
		if (auxTypeLink) {
			setTypeOfLink(auxTypeLink)
		}
		setValue('label', totalData.delivery_enhancements.good_link.link_value)
		if (totalData.delivery_enhancements.good_link.link_type !== 'phone') {
			setValue(
				'url',
				totalData.delivery_enhancements.good_link.link_call_action
			)
		} else {
			setValue(
				'phoneNumber',
				parseToDisplay(
					totalData.delivery_enhancements.good_link.link_call_action || ''
				)
			)
		}

		if (totalData.delivery_enhancements.good_link.is_sublink) {
			setSubLink(totalData.delivery_enhancements.good_link.is_sublink)
			const auxTypeSubLink = typeOfLinkData.find(
				item =>
					item.value === totalData.delivery_enhancements.good_link.sublink_type
			)
			if (auxTypeSubLink) {
				setTypeOfSubLink(auxTypeSubLink)
			}
			setValue(
				'subLabel',
				totalData.delivery_enhancements.good_link.sublink_value
			)
			if (totalData.delivery_enhancements.good_link.sublink_type !== 'phone') {
				setValue(
					'subUrl',
					totalData.delivery_enhancements.good_link.sublink_call_action
				)
			} else {
				setValue(
					'subPhoneNumber',
					parseToDisplay(
						totalData.delivery_enhancements.good_link.sublink_call_action || ''
					)
				)
			}
		}
	}, [totalData])

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="mt-5 flex flex-col justify-between h-full"
		>
			<div>
				<Typography
					title="Main Link"
					type={TypographyTypes.h3}
					className="text-gray-9 !font-bold"
					fontLeading="15/18"
				/>
				<div className="mt-2">
					<Typography
						title="Type of Link"
						type={TypographyTypes.h3}
						className="text-gray-9 !font-bold mb-2"
						fontLeading="12/12"
					/>
					<DropdownFull
						item={typeOfLink}
						setClean={setClean}
						list={typeOfLinkData}
						setItem={setTypeOfLink}
					/>
				</div>
				<div className="border-b-[0.5px] border-gray-3 mt-3">
					{typeOfLink.id === 1 && (
						<Input
							name="url"
							title="URL"
							register={register}
							rules={rules.url}
							placeholder="Insert Link"
							error={errors.url}
							className="my-4"
						/>
					)}
					{typeOfLink.id === 2 && (
						<Input
							name="url"
							title="AllAboard Link"
							register={register}
							rules={rules.url}
							placeholder="Insert Link"
							error={errors.url}
							className="my-4"
						/>
					)}
					{typeOfLink.id === 3 && (
						<Input
							name="url"
							title="Email Address"
							type="email"
							register={register}
							rules={rules.email}
							placeholder="example@gmail.com"
							error={errors.email}
							className="my-4"
						/>
					)}
					{typeOfLink.id === 4 && (
						<InputPhoneFull
							name="phoneNumber"
							title="Phone Number"
							register={register}
							setValueInput={setValue}
							rules={rules.phoneNumber}
							error={errors.phoneNumber}
							placeholder={PHONE_PLACEHOLDER}
						/>
					)}
					{typeOfLink.id === 5 && (
						<Button
							type="button"
							label="Upload"
							color="Gray"
							iconLeft={Icons.upload}
							className="w-full"
							// onClick={onClickClose}
						/>
					)}
					{typeOfLink.id !== 0 && (
						<Input
							name="label"
							title="GoodLink Label"
							register={register}
							rules={rules.label}
							placeholder="Type your call to action here"
							error={errors.label}
							className="my-4"
							limit
							cantLimit={25}
							watchValue={label}
							setValueInput={setValue}
						/>
					)}
				</div>
				<BoxSwitch title="Sub Link" enabled={subLink} setEnabled={setSubLink} />
				{subLink && (
					<div>
						<div className="mt-2">
							<Typography
								title="Type of Link"
								type={TypographyTypes.h3}
								className="text-gray-9 !font-bold mb-2"
								fontLeading="12/12"
							/>
							<DropdownFull
								item={typeOfSubLink}
								list={typeOfLinkData}
								setClean={setSubClean}
								setItem={setTypeOfSubLink}
							/>
						</div>
						{typeOfSubLink.id === 1 && (
							<Input
								name="subUrl"
								title="URL"
								register={register}
								rules={rules.subUrl}
								placeholder="Insert Link"
								error={errors.subUrl}
								className="my-4"
							/>
						)}
						{typeOfSubLink.id === 2 && (
							<Input
								name="subUrl"
								title="AllAboard Link"
								register={register}
								rules={rules.subUrl}
								placeholder="Insert Link"
								error={errors.subUrl}
								className="my-4"
							/>
						)}
						{typeOfSubLink.id === 3 && (
							<Input
								name="subUrl"
								title="Email Address"
								type="email"
								register={register}
								rules={rules.subEmail}
								placeholder="example@gmail.com"
								error={errors.subEmail}
								className="my-4"
							/>
						)}
						{typeOfSubLink.id === 4 && (
							<InputPhoneFull
								name="subPhoneNumber"
								title="Phone Number"
								register={register}
								setValueInput={setValue}
								rules={rules.subPhoneNumber}
								error={errors.subPhoneNumber}
								placeholder={PHONE_PLACEHOLDER}
							/>
						)}
						{typeOfSubLink.id === 5 && (
							<Button
								type="button"
								label="Upload"
								color="Gray"
								iconLeft={Icons.upload}
								className="w-full"
								// onClick={onClickClose}
							/>
						)}
						{typeOfSubLink.id === 5 && (
							<Button
								color="Gray"
								type="button"
								label="Upload"
								className="w-full"
								iconLeft={Icons.upload}
							/>
						)}
						{typeOfSubLink.id !== 0 && (
							<Input
								name="subLabel"
								title="GoodLink Label"
								register={register}
								rules={rules.subLabel}
								placeholder="Type your call to action here"
								error={errors.subLabel}
								className="my-4"
								limit
								cantLimit={25}
								watchValue={subLabel}
								setValueInput={setValue}
							/>
						)}
					</div>
				)}
			</div>
			<div className="flex items-center justify-between">
				<Button
					type="button"
					label="Cancel"
					color="Transparent"
					className="!min-w-[130px]"
					onClick={onClickClose}
				/>
				<Button
					label="Save"
					type="submit"
					color="Primary"
					className="!min-w-[130px]"
					disabled={!isSubmit}
				/>
			</div>
		</form>
	)
}
